import React from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import {productHeadingText} from '../components/product-heading';
import {getPriceDisplay} from '../pricing-request/price-display';
import PricingStatus from '../components/pricing-status';
import SliderRealImages from '../components/slider/slider-real-images';
import {isFixedSizeDisplay} from '../components/doc-sizes-and-categories/document-sizing';
import {isNullOrWhiteSpace, modal} from '../../util';
import Gallery from './gallery';
import {CheckCircleIcon, RulerIcon, Stars} from './icons';
import {answerSteps, fitText} from './copy';
import {degreeParts} from './size-questions';
import {redesign} from './redesign-store';
import {stepPlan} from './steps-plan';

declare var _msrp_red: boolean;

/** The "N reviews" link: the reviews section (expanded) in view. */
export const goToReviews = (e?: React.MouseEvent): void => {
    e?.preventDefault();
    const main = document.getElementById('yotpo-main');
    if (main && !main.classList.contains('show')) {
        document.getElementById('prod-reviews-click')?.click();
    }
    setTimeout(() => document.getElementById('prod-reviews')?.scrollIntoView({behavior: 'smooth', block: 'start'}), 100);
};

/** The item number as the control header shows it. */
export const itemNumber = (): string => s.ui.item_id.replace('215873-', 'P').replace('220395-', 'P');

/** The degree name the size fits: the selected size row's DocSizeName, else the product's degree type; only a list of degrees. */
const fitDegreeName = (): string => {
    const row = s.docsizes_enabled ? s.docsizes_available.find(d => d.Id === s.docsizes_selected_id) : undefined;
    const name = (row?.DocSizeName || s.prod.DegreeType || '').trim();
    return degreeParts(name) ? name : '';
};

/** The document the frame holds: "diploma" on the products the page treats as diplomas (their title says so), else DocType. */
export const documentWord = (): string => (/diploma/i.test(s.ui.product_title || '') ? 'diploma' : (s.ui.doc_type || 'document').trim().toLowerCase());

/** "Made to fit your NYU diploma. Bachelor's, Master's and PhD, 14.25" x 11.25". No measuring needed. Different size?" */
export const FitBox: React.FC = observer(() => {
    if (isFixedSizeDisplay(s.prod) || !(s.ps.docWidth > 0) || !(s.ps.docHeight > 0)) {
        return null;
    }

    const open = redesign.open;
    if (open.length > 0) {
        const plan = stepPlan();
        const numbers = plan.steps.filter(step => (open as string[]).includes(step.id)).map(step => step.number);
        return (
            <div className="pp3-fit pp3-fit-open">
                <RulerIcon className="pp3-fit-icon" />
                <p>
                    <strong>No measuring needed.</strong> {answerSteps(numbers)} and the frame size sets itself.
                </p>
            </div>
        );
    }

    const text = fitText({
        school: !isNullOrWhiteSpace(s.si.ProdInstText) ? s.si.ProdInstText : s.si.InstName,
        docType: documentWord(),
        degreeName: fitDegreeName(),
        width: s.ps.docWidth,
        height: s.ps.docHeight,
    });

    return (
        <div className="pp3-fit">
            <CheckCircleIcon className="pp3-fit-icon" />
            <p>
                <strong>{text.lead}</strong> {text.detail} No measuring needed.{' '}
                <button
                    type="button"
                    className="pp3-link"
                    onClick={() => modal('modal-doc-size-diff')}
                    aria-haspopup="dialog"
                    aria-controls="modal-doc-size-diff"
                >
                    Different size?
                </button>
            </p>
        </div>
    );
});

/** The rating as read out: one decimal ("Rated 4.8 out of 5"). _ProductShellB.cshtml writes the same text. */
export const ratingText = (score: number): string => String(Math.round(score * 10) / 10);

/** The heading block: H1, the rating and item number, the price (the struck list price on a discount). */
const Headline: React.FC = observer(() => {
    const price = getPriceDisplay(s.ui);
    const showReviews = s.ui.allow_reviews && s.ui.review_count > 0;

    return (
        <>
            <h1 className="pp3-title">{productHeadingText()}</h1>
            <div className="pp3-headline">
                <div className="pp3-meta">
                    {showReviews && (
                        <span className="pp3-rating">
                            <span className="sr-only">Rated {ratingText(s.ui.review_score)} out of 5</span>
                            <Stars score={s.ui.review_score} />
                            <a
                                href="#reviews"
                                onClick={goToReviews}
                            >
                                {s.ui.review_count} review{s.ui.review_count === 1 ? '' : 's'}
                            </a>
                        </span>
                    )}
                    {!s.ps.is_caf && <span className="pp3-item">Item #{itemNumber()}</span>}
                    {!isNullOrWhiteSpace(s.ui.sku) && <span className="pp3-item">SKU: {s.ui.sku}</span>}
                </div>
                {s.ui.show_pricing && (
                    <div
                        className="pp3-price"
                        aria-busy={s.ui.pricing_loading}
                    >
                        {price.hasDiscount ? (
                            <>
                                <span className={_msrp_red ? 'pp3-price-now pp3-price-sale' : 'pp3-price-now'}>
                                    <span className="sr-only">Sale price </span>
                                    {price.current}
                                </span>
                                <del className="pp3-price-was">
                                    <span className="sr-only">Regular price </span>
                                    {price.original}
                                </del>
                            </>
                        ) : (
                            <span className="pp3-price-now">{price.current}</span>
                        )}
                    </div>
                )}
            </div>
            {s.ui.show_pricing && <PricingStatus error={s.ui.pricing_error} />}
        </>
    );
});

/** "Customer photos": the control's real-image slider under the redesign's heading. */
export const CustomerPhotos: React.FC = () => (
    <section
        className="pp3-photos"
        aria-labelledby="pp3-photos-heading"
    >
        <div className="pp3-photos-head">
            <h2 id="pp3-photos-heading">Customer photos</h2>
            <span>Tag us and share your pics with #EarnItFrameIt</span>
        </div>
        <SliderRealImages />
    </section>
);

/** The left column: the heading block, "Made to fit", the gallery and (two columns) the customer photos. */
const Overview: React.FC<{photos: boolean}> = observer(({photos}) => (
    <div
        className="pp3-overview"
        role="region"
        aria-label="Product overview"
    >
        <Headline />
        <div className="pp3-fit-slot">
            <FitBox />
        </div>
        <Gallery />
        {photos && <CustomerPhotos />}
    </div>
));

export default Overview;
